// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SwiperClass from "swiper/types/swiper-class";

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@keller/App/Components/Notify";
import TitleTypography, { TitleTypographyProps } from "@keller/App/Components/TitleTypography/TitleTypography";
import XngCarousel from "@keller/App/Components/XngCarousel/XngCarousel";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@keller/Overrides/Components/ProductCarouselProduct";
import { MappedLink } from "@keller/Overrides/Widgets/Header/MainNavigation";
import { color, mergeWithCss } from "@keller/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    setSwiperInstance?: (swiperInstance: SwiperClass | null) => void;
    link: MappedLink;
    extendedStyles?: RecentlyPurchasedStyles;
}

type Props = OwnProps;

export interface RecentlyPurchasedStyles {
    titleText?: TitleTypographyProps;
    container?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
}

const recentlyPurchasedStyle: RecentlyPurchasedStyles = {
    container: {
        css: css`
            width: 100%;
            min-width: 600px;
            background-color: ${color("common.white")};
        `,
    },
    carouselContainer: {
        css: css`
            margin: auto;
        `,
    },
    carouselProductStyles: {
        productGridLayoutStyle: {
            container: {
                css: css`
                    max-width: 288px;
                    min-width: 288px;
                    padding: 0 5px;
                `,
            },
        },
    },
};

const RecentlyPurchased: FC<Props> = ({ link, setSwiperInstance, extendedStyles }) => {
    const products = useAppSelector(state => getProductsForProductInfoList(state, HeaderRecentlyPurchasedWidgetId));
    const [styles] = useState(() => mergeWithCss(recentlyPurchasedStyle, extendedStyles));

    return (
        <StyledWrapper {...styles.container}>
            <TitleTypography
                {...styles.titleText}
                link={link.url}
                openInNewTab={link.openInNewWindow}
                linkText={translate("View All Previously Purchased")}
            >
                {translate("Previously Purchased")}
            </TitleTypography>
            {products && products.length > 0 ? (
                <StyledWrapper {...styles.carouselContainer}>
                    <XngCarousel
                        onSwiper={setSwiperInstance}
                        spaceBetween={35}
                        slidesPerView={products.length < 4 ? products.length : 4}
                        navigation={false}
                        pagination={false}
                    >
                        {products.map(product => (
                            <ProductCarouselProduct
                                key={product.id}
                                carouselId={HeaderRecentlyPurchasedWidgetId}
                                product={product}
                                showImage={true}
                                showBrand={true}
                                showTitle={true}
                                showPartNumbers={true}
                                showPrice={true}
                                showAddToCart={true}
                                showAddToList={false}
                                extendedStyles={styles.carouselProductStyles}
                            />
                        ))}
                    </XngCarousel>
                </StyledWrapper>
            ) : (
                <Notify type="info">{translate("No products found")}</Notify>
            )}
        </StyledWrapper>
    );
};

export default RecentlyPurchased;

export const HeaderRecentlyPurchasedWidgetId = "header-RecentlyPurchased";
